/** The record editor's built-in widgets by catalog `widget` name. Remote pickers (tour,
 *  role, tags, user) belong to a record kind and arrive through
 *  `RecordEditorConfig.widgets` (see `pickers.tsx`); the contacts list is built in.
 *
 *  A widget is its definition: the input, plus whatever protocol it declares on it
 *  (`RecordWidgetProtocol` — its own format, checks, ledger lines, what it marks itself).
 *  The editor reaches it through `useRecordEditor().widgetOf(field)`, never by name.
 *
 *  The editor provider imports this registry: a module it loads takes `useRecordEditor`
 *  from `editor/context`, never from the provider (that would be an import cycle). */
import type { RecordWidget } from '../../editor/types';

import { BooleanWidget, CountryWidget, EnumWidget } from './ChoiceWidgets';
import { ContactsWidget } from './ContactsWidget';
import { DurationRangeWidget } from './DurationRangeWidget';
import { OpeningHoursWidget } from './OpeningHoursWidget';
import { RichTextWidget } from './RichTextWidget';
import { DateTimeWidget, DateWidget, TimeWidget } from './TemporalWidgets';
import { DecimalWidget, IntegerWidget, TextareaWidget, TextLazyWidget, TextWidget } from './TextWidgets';

export const BUILTIN_WIDGETS: Readonly<Record<string, RecordWidget>> = {
  text: TextWidget,
  email: TextWidget,
  textarea: TextareaWidget,
  text_lazy: TextLazyWidget,
  // Stored HTML: read in place, the shared Quill editor only once clicked. The catalog
  // records' edit catalogs name it `rich_text` (the tour's own catalog, `richtext`).
  richtext: RichTextWidget,
  rich_text: RichTextWidget,
  integer: IntegerWidget,
  // A decimal (a catalog record's price): at most two decimals as it reads.
  number: DecimalWidget,
  boolean: BooleanWidget,
  enum: EnumWidget,
  country: CountryWidget,
  date: DateWidget,
  time: TimeWidget,
  datetime: DateTimeWidget,
  // The contacts list (a person's emails, phones and URLs) — its record kind supplies the
  // account and the stored contacts' facts through `ContactsProvider`.
  contacts: ContactsWidget,
  // An activity's week of opening hours (the app's periods list) — it captions its own
  // errors, on the day they concern.
  opening_hours: OpeningHoursWidget,
  // A visit's duration: ONE key, `{min, max}` in hours.
  duration: DurationRangeWidget,
};

/** Widgets whose value is a local wall clock — the label row names the zone. */
export const TEMPORAL_WIDGETS: ReadonlySet<string> = new Set(['date', 'time', 'datetime']);
